import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ApiError, getApiErrorPresentation } from '@/services/api';
import { AlertCircle, Loader2, RotateCcw } from 'lucide-react';
import { useEffect } from 'react';

export interface DashboardWidgetContext {
  portal: 'admin' | 'agent' | 'affiliate' | 'member' | 'individual';
  widget: string;
  endpoint: string;
}

const reportedErrorContexts = new WeakMap<object, Set<string>>();

interface DashboardWidgetErrorProps {
  title: string;
  description: string;
  error: unknown;
  context: DashboardWidgetContext;
  onRetry: () => void;
  isRetrying?: boolean;
  embedded?: boolean;
  className?: string;
}

export function reportDashboardWidgetError(error: unknown, context: DashboardWidgetContext): void {
  const diagnostic =
    error instanceof ApiError
      ? {
          errorKind: error.kind,
          status: error.status,
          requestId: error.requestId,
        }
      : {
          errorType: error instanceof Error ? error.name : typeof error,
        };

  console.error('[DashboardWidget] Request failed', {
    ...context,
    ...diagnostic,
  });
}

function shouldReportDashboardWidgetError(
  error: unknown,
  context: DashboardWidgetContext
): boolean {
  if ((typeof error !== 'object' && typeof error !== 'function') || error === null) return true;

  const contextKey = `${context.portal}:${context.widget}:${context.endpoint}`;
  const seenContexts = reportedErrorContexts.get(error);
  if (seenContexts?.has(contextKey)) return false;

  if (seenContexts) {
    seenContexts.add(contextKey);
  } else {
    reportedErrorContexts.set(error, new Set([contextKey]));
  }
  return true;
}

export function DashboardWidgetError({
  title,
  description,
  error,
  context,
  onRetry,
  isRetrying = false,
  embedded = false,
  className,
}: DashboardWidgetErrorProps) {
  const apiPresentation = error instanceof ApiError ? getApiErrorPresentation(error) : null;
  const retryLabel = apiPresentation ? apiPresentation.retryLabel : 'Retry';

  useEffect(() => {
    if (shouldReportDashboardWidgetError(error, context)) {
      reportDashboardWidgetError(error, context);
    }
  }, [error, context]);

  const content = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
        <div>
          <p className="font-medium text-foreground">{apiPresentation?.title ?? title}</p>
          {apiPresentation && (
            <p className="mt-1 text-sm text-muted-foreground">{apiPresentation.description}</p>
          )}
          {apiPresentation && <p className="mt-2 text-sm font-medium text-foreground">{title}</p>}
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          {error instanceof ApiError && error.requestId && (
            <p className="mt-2 break-all font-mono text-xs text-muted-foreground">
              Reference: {error.requestId}
            </p>
          )}
        </div>
      </div>
      {retryLabel && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full shrink-0 sm:w-auto"
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {isRetrying ? 'Retrying…' : retryLabel}
        </Button>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className={cn('py-1', className)} role="alert">
        {content}
      </div>
    );
  }

  return (
    <Card className={className} role="alert">
      <CardContent className="p-5">{content}</CardContent>
    </Card>
  );
}

export function DashboardWidgetLoading({
  label,
  embedded = false,
}: {
  label: string;
  embedded?: boolean;
}) {
  const content = (
    <div className="flex min-h-28 items-center justify-center gap-3 text-sm text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
      {label}
    </div>
  );

  if (embedded) {
    return (
      <div aria-busy="true" aria-live="polite">
        {content}
      </div>
    );
  }

  return (
    <Card aria-busy="true" aria-live="polite">
      <CardContent className="p-5">{content}</CardContent>
    </Card>
  );
}
